import type { ReactNode } from "react";
import {
  ActivityIndicator,
  Pressable,
  Text,
  type PressableProps,
  type StyleProp,
  type TextStyle,
  type ViewStyle,
} from "react-native";
import { EMBER, colors } from "../theme/tokens";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

export type CompactActionButtonSize = "md" | "sm";
export type CompactActionButtonVariant =
  | "primary"
  | "secondary"
  | "selected"
  /** Reserved for purchase CTAs once checkout is fully wired. */
  | "commerce"
  | "modalPrimary"
  | "modalSecondary";

export type CompactActionButtonProps = Omit<
  PressableProps,
  "children" | "style"
> & {
  label: string;
  size?: CompactActionButtonSize;
  /** Stable analytics identifier, required when `label` is translated. */
  action?: string;
  variant?: CompactActionButtonVariant;
  loading?: boolean;
  leading?: ReactNode;
  icon?: ReactNode;
  trailing?: ReactNode;
  disabledOpacity?: number;
  pressedOpacity?: number;
  activityIndicatorColor?: string;
  numberOfLines?: number;
  style?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
};

const SIZE_STYLE: Record<
  CompactActionButtonSize,
  {
    minHeight: number;
    paddingVertical: number;
    paddingHorizontal: number;
    gap: number;
    fontSize: number;
    lineHeight?: number;
  }
> = {
  md: {
    minHeight: 38,
    paddingVertical: 10,
    paddingHorizontal: 13,
    gap: 6,
    fontSize: 12.5,
    lineHeight: 16,
  },
  sm: {
    minHeight: 34,
    paddingVertical: 7,
    paddingHorizontal: 11,
    gap: 6,
    fontSize: 12,
  },
};

const VARIANT_STYLE: Record<
  CompactActionButtonVariant,
  {
    backgroundColor: string;
    pressedBackgroundColor: string;
    borderColor: string;
    textColor: string;
    activityIndicatorColor: string;
  }
> = {
  primary: {
    backgroundColor: "rgba(92,52,32,0.1)",
    pressedBackgroundColor: "rgba(92,52,32,0.15)",
    borderColor: "rgba(92,52,32,0.18)",
    textColor: EMBER.colors.text,
    activityIndicatorColor: EMBER.colors.text,
  },
  selected: {
    backgroundColor: "rgba(42, 37, 34, 0.1)",
    pressedBackgroundColor: "rgba(42, 37, 34, 0.14)",
    borderColor: "rgba(42, 37, 34, 0.24)",
    textColor: EMBER.colors.text,
    activityIndicatorColor: EMBER.colors.text,
  },
  secondary: {
    backgroundColor: "rgba(255,255,255,0.48)",
    pressedBackgroundColor: "rgba(255,255,255,0.72)",
    borderColor: "rgba(92,52,32,0.1)",
    textColor: EMBER.colors.text,
    activityIndicatorColor: EMBER.colors.text,
  },
  commerce: {
    backgroundColor: "rgba(255,255,255,0.3)",
    pressedBackgroundColor: "rgba(255,255,255,0.5)",
    borderColor: "rgba(120,120,120,0.22)",
    textColor: EMBER.colors.text,
    activityIndicatorColor: EMBER.colors.text,
  },
  modalPrimary: {
    backgroundColor: "rgba(255, 255, 255, 0.28)",
    pressedBackgroundColor: "rgba(255, 255, 255, 0.28)",
    borderColor: "rgba(42, 37, 34, 0.1)",
    textColor: colors.greyBrown,
    activityIndicatorColor: colors.greyBrown,
  },
  modalSecondary: {
    backgroundColor: "rgba(255, 255, 255, 0.22)",
    pressedBackgroundColor: "rgba(255, 255, 255, 0.22)",
    borderColor: "rgba(42, 37, 34, 0.1)",
    textColor: colors.greyBrown,
    activityIndicatorColor: colors.greyBrown,
  },
};

export function CompactActionButton({
  label,
  onPress,
  size = "md",
  variant = "primary",
  loading = false,
  leading,
  icon,
  trailing,
  disabled = false,
  disabledOpacity = 0.58,
  pressedOpacity = 0.78,
  activityIndicatorColor,
  action,
  accessibilityLabel,
  numberOfLines,
  style,
  textStyle,
  ...pressableProps
}: CompactActionButtonProps) {
  const sizeStyle = SIZE_STYLE[size];
  const variantStyle = VARIANT_STYLE[variant];
  const isInactive = disabled || loading;
  const capturePress = usePressCapture("CompactActionButton");

  return (
    <Pressable
      {...pressableProps}
      onPress={(event) => {
        capturePress(pressLabel(label, accessibilityLabel), action);
        onPress?.(event);
      }}
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityRole="button"
      accessibilityState={{ disabled: isInactive, busy: loading }}
      disabled={isInactive}
      style={({ pressed }) => [
        {
          minHeight: sizeStyle.minHeight,
          borderRadius: 999,
          borderWidth: 1,
          borderColor: variantStyle.borderColor,
          backgroundColor: pressed
            ? variantStyle.pressedBackgroundColor
            : variantStyle.backgroundColor,
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          gap: sizeStyle.gap,
          paddingHorizontal: sizeStyle.paddingHorizontal,
          paddingVertical: sizeStyle.paddingVertical,
          opacity: loading
            ? 0.7
            : disabled
              ? disabledOpacity
              : pressed
                ? pressedOpacity
                : 1,
        },
        style,
      ]}
    >
      {loading ? (
        <ActivityIndicator
          size="small"
          color={activityIndicatorColor ?? variantStyle.activityIndicatorColor}
        />
      ) : (
        (leading ?? icon)
      )}
      <Text
        className="font-dm-sans"
        numberOfLines={numberOfLines}
        style={[
          {
            fontSize: sizeStyle.fontSize,
            lineHeight: sizeStyle.lineHeight,
            color: variantStyle.textColor,
            fontWeight: "800",
            textAlign: "center",
          },
          textStyle,
        ]}
      >
        {label}
      </Text>
      {trailing}
    </Pressable>
  );
}

export default CompactActionButton;
